/**
 * Quiz Detox Integrale - State Machine
 * Controls quiz flow, state management, and user interactions
 *
 * Flow: Welcome -> Questions (Q1-Q4) -> Interstitial -> Questions (Q5-Q15) -> Analysis -> Redirect
 */

(function() {
  'use strict';

  var config = null;
  // Capture original URL params BEFORE any pushState changes them
  var originalSearch = window.location.search;
  var originalParams = new URLSearchParams(originalSearch);
  var quizState = {
    currentQuestion: 0,
    totalQuestions: 0, // overwritten in initQuiz() from config.questions.length
    responses: [],
    isTransitioning: false,
    startTime: null,
    selectedAnswers: [],
    completed: false,
    interstitialShown: false,
    interstitialsShown: {},
    // v6 T1
    commitmentShown: false,
    commitmentAnswer: null,
    // v6.6 — milestone toasts already shown this session (keyed by afterQuestion)
    milestonesShown: {}
  };

  var elements = {};

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initQuiz);
  } else {
    initQuiz();
  }

  function initQuiz() {
    config = window.quizDetoxConfig;
    if (!config) {
      console.error('Quiz Detox configuration not found');
      return;
    }

    quizState.totalQuestions = config.questions.length;
    cacheElements();
    loadState();
    // Always use current config question count (sessionStorage may have stale value)
    quizState.totalQuestions = config.questions.length;
    setupEventListeners();
    setViewportHeight();
    window.addEventListener('resize', setViewportHeight);

    // Show CTA if user has a previous result (even on fresh page load)
    if (sessionStorage.getItem('quizDetoxResult')) {
      showPreviousResultCta();
    }

    var urlParams = new URLSearchParams(window.location.search);

    // v5: no welcome screen — Q1 is the entry point. Liquid set questionScreen active.
    // logWelcomeViewed() still fires (denominator) but unconditionally on first render,
    // not gated on welcome being visible. startQuiz() then runs automatically so Q1
    // renders without a CTA click.
    var isV5 = (config.version || 1) >= 5;
    var isMidQuizResume = (quizState.currentQuestion > 0 || quizState.completed);
    var isDevShortcut = (urlParams.get('q') === 'loader') || urlParams.has('force');

    if (isV5) {
      if (!isMidQuizResume && !isDevShortcut) {
        logWelcomeViewed();
        startQuiz(null);
      }
    } else {
      // v<5: existing behavior — log welcome only when welcome is the active screen.
      if (elements.welcomeScreen && elements.welcomeScreen.classList.contains('active') &&
          !isDevShortcut) {
        logWelcomeViewed();
      }
    }

    // Dev/debug shortcut: ?q=loader  →  jump straight to the loading screen with
    // synthetic mid-range responses so axis scores are non-zero and look realistic.
    if (urlParams.get('q') === 'loader') {
      seedFakeResponsesForLoaderPreview();
      setTimeout(function() {
        showScreen('loading');
        animateLoadingScreen();
      }, 50);
      return;
    }

    // Dev/debug: ?force&q=N or ?force&screen=interstitial|loading|results
    if (urlParams.has('force')) {
      var forceQ = parseInt(urlParams.get('q'), 10);
      var forceScreen = urlParams.get('screen');
      var forceAfter = parseInt(urlParams.get('after'), 10) || 4;
      setTimeout(function() {
        if (forceScreen === 'interstitial') {
          quizState.interstitialsShown = {};
          showInterstitial(forceAfter);
        } else if (forceScreen === 'loading') {
          showScreen('loading');
          animateLoadingScreen();
        } else if (forceScreen === 'results') {
          showScreen('results');
        } else if (forceQ && forceQ > 0 && forceQ <= quizState.totalQuestions) {
          quizState.currentQuestion = forceQ;
          quizState.gender = quizState.gender || 'female';
          quizState.responses = [];
          quizState.selectedAnswers = [];
          quizState.completed = false;
          quizState.isTransitioning = false;
          saveState();
          showScreen('question');
          renderQuestion(forceQ);
          updateProgress();
        }
      }, 100);
      // Strip ?force from URL so it can't be shared
      window.history.replaceState({}, '', window.location.pathname);
    }
  }

  function cacheElements() {
    elements = {
      container: document.getElementById('quizContainer'),
      welcomeScreen: document.getElementById('welcomeScreen'),
      questionScreen: document.getElementById('questionScreen'),
      interstitialScreen: document.getElementById('interstitialScreen'),
      commitmentScreen: document.getElementById('commitmentScreen'),
      commitmentTitle: document.getElementById('commitmentTitle'),
      commitmentBody: document.getElementById('commitmentBody'),
      commitmentYesBtn: document.getElementById('commitmentYesBtn'),
      commitmentNoBtn: document.getElementById('commitmentNoBtn'),
      // v6.1 T7 — Results Summary screen elements
      resultsSummaryScreen: document.getElementById('resultsSummaryScreen'),
      resultsIntro: document.getElementById('resultsIntro'),
      resultsAxesContainer: document.getElementById('resultsAxesContainer'),
      resultsDominantLabel: document.getElementById('resultsDominantLabel'),
      resultsDominantValue: document.getElementById('resultsDominantValue'),
      resultsSolutionTitle: document.getElementById('resultsSolutionTitle'),
      resultsSolutionBody: document.getElementById('resultsSolutionBody'),
      // v6.5 — milestones block (replaces v6.4's single offer block)
      resultsMilestones: document.getElementById('resultsMilestones'),
      resultsMilestonesTitle: document.getElementById('resultsMilestonesTitle'),
      resultsMilestonesSub: document.getElementById('resultsMilestonesSub'),
      resultsMilestonesList: document.getElementById('resultsMilestonesList'),
      resultsCtaBtn: document.getElementById('resultsCtaBtn'),
      loadingScreen: document.getElementById('loadingScreen'),
      // v6.3 persona-adaptive loading testimonial
      loadingTestimonialQuote: document.getElementById('loadingTestimonialQuote'),
      loadingTestimonialAuthor: document.getElementById('loadingTestimonialAuthor'),
      // v6.6 milestone toast (drip-fed earned rewards mid-quiz)
      milestoneToast: document.getElementById('milestoneToast'),
      milestoneToastIcon: document.getElementById('milestoneToastIcon'),
      milestoneToastLabel: document.getElementById('milestoneToastLabel'),
      resultsScreen: document.getElementById('resultsScreen'),
      startBtn: document.getElementById('startQuizBtn'),
      startQuizCta: document.getElementById('startQuizCta'),
      // Legacy v1 gender buttons (kept for backward compat when config_version=1)
      genderBtnHomme: document.getElementById('genderBtnHomme'),
      genderBtnFemme: document.getElementById('genderBtnFemme'),
      backBtn: document.getElementById('backBtn'),
      questionNumber: document.getElementById('questionNumber'),
      questionText: document.getElementById('questionText'),
      answersContainer: document.getElementById('answersContainer'),
      progressBar: document.getElementById('quizProgressBar'),
      progressFill: document.getElementById('quizProgressFill'),
      loadingMessage: document.getElementById('loadingMessage'),
      loadingProgressFill: document.getElementById('loadingProgressFill'),
      interstitialBtn: document.getElementById('interstitialContinueBtn'),
      restartQuizBtn: document.getElementById('restartQuizBtn')
    };
  }

  function setupEventListeners() {
    if (elements.startBtn) {
      elements.startBtn.addEventListener('click', function() { startQuiz(null); });
    }
    // v2 single CTA — no gender at entry (gender asked as a question inside the quiz)
    if (elements.startQuizCta) {
      elements.startQuizCta.addEventListener('click', function() { startQuiz(null); });
    }
    // v1 gender buttons (legacy: kept for rollback / archived v1 template)
    if (elements.genderBtnHomme) {
      elements.genderBtnHomme.addEventListener('click', function() { startQuiz('male'); });
    }
    if (elements.genderBtnFemme) {
      elements.genderBtnFemme.addEventListener('click', function() { startQuiz('female'); });
    }
    if (elements.backBtn) {
      elements.backBtn.addEventListener('click', goBack);
    }
    if (elements.interstitialBtn) {
      elements.interstitialBtn.addEventListener('click', continueAfterInterstitial);
    }
    if (elements.restartQuizBtn) {
      elements.restartQuizBtn.addEventListener('click', function() {
        sessionStorage.removeItem('quizDetoxState');
        // v6.6.2 — also drop quizSessionId so the new attempt gets a fresh ID.
        // Without this, retake-in-same-tab inflates v6 counts (same session_id ends
        // up tagged across multiple attempts, and session-based dashboard filters
        // pull both old and new rows into the v6 bucket).
        sessionStorage.removeItem('quizSessionId');
        sessionStorage.removeItem('quizDetoxCartWritten');
        // v6.6.3 — drop the welcome-once guard so the new session_id gets its own
        // welcome_viewed row. Otherwise the welcome→started conversion rate on the
        // dashboard is broken for any retake-in-same-tab.
        sessionStorage.removeItem('quizDetoxWelcomeLogged');
        window.location.reload();
      });
    }
    window.addEventListener('popstate', handlePopState);
  }

  function startQuiz(gender) {
    quizState.startTime = Date.now();
    quizState.currentQuestion = 1;
    quizState.responses = [];
    quizState.selectedAnswers = [];
    quizState.completed = false;
    quizState.startedSent = false;
    quizState.interstitialShown = false;
    quizState.interstitialsShown = {};
    quizState.commitmentShown = false;
    quizState.commitmentAnswer = null;
    quizState.milestonesShown = {};
    quizState.gender = gender || null;

    // Generate session ID for this quiz attempt
    var sessionId = getOrCreateSessionId();
    // Reset cart written flag for new attempt
    sessionStorage.removeItem('quizDetoxCartWritten');

    saveState();

    // Record quiz start.
    // v<5: 'started' = clicked the CTA from welcome (any user who reached Q1 view).
    // v>=5: 'started' (both GA + BQ) is deferred to advanceQuiz() when user moves past
    // Q1 — that's the "actively engaged" signal, comparable in role but stricter than a
    // CTA click. GA and BQ kept aligned to avoid analytics divergence between tools.
    if ((config.version || 1) < 5) {
      trackEvent('quiz_started', { quiz_id: config.quizId, question: 1, gender: gender });
      sendToBigQuery('started', 1);
    }

    showScreen('question');

    setTimeout(function() {
      renderQuestion(1);
      updateProgress();
    }, 50);

    safePushState({ screen: 'question', question: 1 }, '', window.location.pathname + '?q=1');
  }

  // v3: compute the answers array for a question, optionally merging in unpicked options
  // from a source question (used by Q2 with includeUnpickedFrom: 1 to inherit Q1's unpicked
  // primaries). Returns question.answers untouched when no inheritance is configured.
  function computeEffectiveAnswers(question) {
    if (!question || !question.includeUnpickedFrom) return question.answers || [];
    var sourceQ = config.questions.find(function(q) { return q.id === question.includeUnpickedFrom; });
    if (!sourceQ) return question.answers || [];
    var pickedIds = (quizState.responses || [])
      .filter(function(r) { return r.questionId === sourceQ.id; })
      .map(function(r) { return r.answerId; });
    var unpicked = (sourceQ.answers || []).filter(function(a) { return pickedIds.indexOf(a.id) === -1; });
    return unpicked.concat(question.answers || []);
  }

  function renderQuestion(questionNumber) {
    var question = config.questions.find(function(q) { return q.id === questionNumber; });
    if (!question) {
      console.error('Question not found:', questionNumber);
      return;
    }

    // v3: dynamic answers — when includeUnpickedFrom is set, prepend the source question's
    // answers that the user did NOT pick (e.g., Q2 inherits the 3 Q1 options not chosen).
    var effectiveAnswers = computeEffectiveAnswers(question);

    // Preserve in-progress selectedAnswers if they belong to THIS question
    // (e.g., user refreshed mid-Q1 after picking 2 symptoms but before Continue).
    // Otherwise, rehydrate from committed responses (e.g., user went back to an
    // already-answered multi-select question).
    var savedInProgress = (quizState.selectedAnswers || []).slice();
    var isSameQuestion  = quizState.currentQuestion === question.id;
    var inProgressValid = isSameQuestion && savedInProgress.length > 0
      && savedInProgress.every(function(sa) {
        return effectiveAnswers.some(function(a) { return a.id === sa.id; });
      });

    quizState.selectedAnswers = [];

    if (question.multiple && inProgressValid) {
      // Restore live in-progress picks (prevents loss on page refresh mid-question)
      savedInProgress.forEach(function(sa) {
        var match = effectiveAnswers.find(function(a) { return a.id === sa.id; });
        if (match) quizState.selectedAnswers.push(match);
      });
    } else if (question.multiple && quizState.responses && quizState.responses.length) {
      // Rehydrate committed picks (back-navigation to answered multi-select)
      var prior = quizState.responses.filter(function(r) { return r.questionId === question.id; });
      prior.forEach(function(r) {
        var match = effectiveAnswers.find(function(a) { return a.id === r.answerId; });
        if (match) quizState.selectedAnswers.push(match);
      });
    }

    // Force clear any stuck hover/focus states from previous question
    if (document.activeElement) document.activeElement.blur();
    elements.answersContainer.innerHTML = '';

    elements.questionNumber.textContent = 'Question ' + questionNumber + ' sur ' + quizState.totalQuestions;
    elements.questionText.textContent = question.text;

    // Subtitle
    var existingSubtitle = elements.questionText.nextElementSibling;
    if (existingSubtitle && existingSubtitle.classList.contains('quiz-question-subtitle')) {
      if (question.subtitle) {
        existingSubtitle.textContent = question.subtitle;
      } else {
        existingSubtitle.remove();
      }
    } else if (question.subtitle) {
      var subtitle = document.createElement('p');
      subtitle.className = 'quiz-question-subtitle';
      subtitle.textContent = question.subtitle;
      elements.questionText.after(subtitle);
    }

    elements.answersContainer.innerHTML = '';

    // Render answers (effectiveAnswers includes inherited unpicked options when applicable)
    var isYesNo = question.format === 'yesno';
    effectiveAnswers.forEach(function(answer) {
      var card = createAnswerCard(question.id, answer, question.multiple, isYesNo);
      elements.answersContainer.appendChild(card);
    });

    // Add yes/no class to container
    if (isYesNo) {
      elements.answersContainer.classList.add('quiz-answers-yesno');
    } else {
      elements.answersContainer.classList.remove('quiz-answers-yesno');
    }

    // v2: optional 2-column layout for multi-select (via question.columns === 2)
    elements.answersContainer.classList.remove('quiz-answers-2col');
    if (question.columns === 2) {
      elements.answersContainer.classList.add('quiz-answers-2col');
    }

    // For multi-select, add continue button (disabled until at least 1 answer selected)
    if (question.multiple) {
      var continueBtn = document.createElement('button');
      continueBtn.className = 'quiz-btn quiz-btn-primary quiz-continue-btn';
      continueBtn.id = 'quizContinueBtn';
      // Last question: change button text to "Découvrir mon résultat"
      // Per-question CTA override (e.g. Q1 in v5 wants "D\u00e9couvrir mon protocole" to echo the hook
      // copy). Falls back to the last-question / standard-question defaults.
      continueBtn.textContent = question.ctaText
        || ((questionNumber === quizState.totalQuestions) ? 'D\u00e9couvrir mon r\u00e9sultat' : 'Continuer');
      continueBtn.style.marginTop = '0';
      continueBtn.disabled = quizState.selectedAnswers.length === 0;
      continueBtn.addEventListener('click', function() {
        if (continueBtn.disabled) return;
        handleMultipleChoiceContinue(question.id);
      });
      elements.answersContainer.appendChild(continueBtn);
    }

    // v3: optional question (e.g., Q2 secondaires) \u2014 explicit "Passer cette question" link
    // below the continue button. Submits an empty response for this Q and advances.
    if (question.optional) {
      var skipLink = document.createElement('a');
      skipLink.href = '#';
      skipLink.id = 'quizSkipLink';
      skipLink.className = 'quiz-skip-link';
      skipLink.textContent = 'Passer cette question';
      // Hide if user already has selections (back-nav case)
      if (quizState.selectedAnswers.length > 0) skipLink.style.display = 'none';
      skipLink.addEventListener('click', function(e) {
        e.preventDefault();
        handleQuestionSkip(question.id);
      });
      elements.answersContainer.appendChild(skipLink);
    }

    // v3: skip shortcut (e.g., Q11 age) \u2014 "Voir mes r\u00e9sultats imm\u00e9diatement" link below
    // the answers; submits an empty response and jumps directly to the loading screen.
    if (question.skipShortcut) {
      var shortcutLink = document.createElement('a');
      shortcutLink.href = '#';
      shortcutLink.className = 'quiz-skip-shortcut';
      shortcutLink.textContent = '\u2192 Voir mes r\u00e9sultats imm\u00e9diatement';
      shortcutLink.addEventListener('click', function(e) {
        e.preventDefault();
        handleQuestionSkip(question.id);
      });
      elements.answersContainer.appendChild(shortcutLink);
    }

    if (elements.backBtn) {
      // No back from Q1 — in v<5 it returned to the welcome screen (always weird UX);
      // in v5 there is no welcome screen at all so back from Q1 is nonsensical.
      elements.backBtn.style.display = (questionNumber === 1) ? 'none' : 'block';
    }

    // v5 entry hook: static title with an animated highlighter that paints across "30 secondes"
    // 400ms after Q1 first renders. Animation fires on EVERY render of Q1 (including reloads)
    // so it's testable — also visually rewards back-nav return to Q1.
    var hookEl = document.getElementById('quizHook');
    var hookHighlightEl = document.getElementById('quizHookHighlight');
    if (hookEl) {
      if (questionNumber === 1 && (config.version || 1) >= 5) {
        hookEl.classList.remove('quiz-hook--hidden');
        if (hookHighlightEl) {
          // Reset then trigger — using requestAnimationFrame ensures the browser sees the
          // 0% → 100% transition rather than collapsing it to a single paint.
          hookHighlightEl.classList.remove('quiz-hook__highlight--active');
          // Force reflow so the next class-add restarts the CSS transition cleanly.
          void hookHighlightEl.offsetWidth;
          setTimeout(function() {
            hookHighlightEl.classList.add('quiz-hook__highlight--active');
          }, 400);
        }
      } else {
        hookEl.classList.add('quiz-hook--hidden');
      }
    }

    // Remove any leftover restart link
    var existingRestart = document.getElementById('quizRestartLink');
    if (existingRestart) {
      existingRestart.remove();
    }

    quizState.currentQuestion = questionNumber;
    saveState();
  }


  function createAnswerCard(questionId, answer, isMultiple, isYesNo) {
    var card = document.createElement('div');
    card.className = 'quiz-answer-card';
    card.setAttribute('data-answer-id', answer.id);
    if (isYesNo) {
      card.classList.add('quiz-answer-yesno');
    }
    card.setAttribute('data-question-id', questionId);
    card.setAttribute('data-answer-id', answer.id);

    if (isMultiple) {
      card.classList.add('quiz-answer-multiple');
      var checkbox = document.createElement('div');
      checkbox.className = 'quiz-answer-checkbox';
      card.appendChild(checkbox);
    }

    var iconContainer = document.createElement('div');
    iconContainer.className = 'quiz-answer-icon';
    if (answer.icon) {
      iconContainer.textContent = answer.icon;
    }

    var text = document.createElement('div');
    text.className = 'quiz-answer-text';
    text.textContent = answer.text;

    card.appendChild(iconContainer);
    card.appendChild(text);

    // Restore .selected state if this answer was previously picked (back-navigation / refresh)
    var wasPicked = quizState.selectedAnswers.some(function(a) { return a.id === answer.id; });
    if (wasPicked) card.classList.add('selected');

    card.addEventListener('click', function(event) {
      if (isMultiple) {
        handleMultipleChoiceClick(questionId, answer, card);
      } else {
        handleAnswerClick(questionId, answer, event);
      }
    });

    return card;
  }

  function handleAnswerClick(questionId, answer, event) {
    if (quizState.isTransitioning) return;
    quizState.isTransitioning = true;

    var clickedCard = event.currentTarget;
    clickedCard.classList.add('selected');

    recordResponse(questionId, [answer]);

    setTimeout(function() {
      advanceQuiz();
      quizState.isTransitioning = false;
    }, 300);
  }

  function handleMultipleChoiceClick(questionId, answer, card) {
    var alreadySelected = card.classList.contains('selected');
    var question = config.questions.find(function(q) { return q.id === questionId; });
    var maxSelect = (question && question.maxSelect) || Infinity;

    if (alreadySelected) {
      card.classList.remove('selected');
      quizState.selectedAnswers = quizState.selectedAnswers.filter(function(a) { return a.id !== answer.id; });
    } else if (answer.exclusive) {
      // Clicking an exclusive answer (e.g., "Aucun de ces facteurs") clears every other selection
      var container = card.parentElement;
      if (container) {
        container.querySelectorAll('.quiz-answer-card.selected').forEach(function(other) {
          if (other !== card) other.classList.remove('selected');
        });
      }
      quizState.selectedAnswers = [answer];
      card.classList.add('selected');
    } else {
      // Clicking a normal answer clears any previously-selected exclusive answer
      quizState.selectedAnswers = quizState.selectedAnswers.filter(function(a) {
        if (a.exclusive) {
          var exclusiveCard = card.parentElement && card.parentElement.querySelector('[data-answer-id="' + a.id + '"]');
          if (exclusiveCard) exclusiveCard.classList.remove('selected');
          return false;
        }
        return true;
      });

      // Enforce maxSelect cap — if already at max, ignore the click + brief shake feedback
      if (quizState.selectedAnswers.length >= maxSelect) {
        card.classList.add('quiz-answer-card--shake');
        setTimeout(function() { card.classList.remove('quiz-answer-card--shake'); }, 400);
        return;
      }

      card.classList.add('selected');
      quizState.selectedAnswers.push(answer);
    }

    // v2: soft gating via disabled button instead of blocking popup
    var btn = document.getElementById('quizContinueBtn');
    var wasDisabled = btn ? btn.disabled : false;
    if (btn) btn.disabled = quizState.selectedAnswers.length === 0;

    // Mobile UX: scroll the CTA into view ONLY on the disabled→enabled transition
    // (first selection), not on every subsequent toggle. Without this guard the page
    // would scroll-snap every time the user deselects + reselects, feeling jumpy.
    // `block: 'nearest'` is a no-op when the button is already visible (desktop case).
    // Note: this applies to ALL versions' multi-select questions, not just v5.
    if (btn && wasDisabled && !btn.disabled) {
      setTimeout(function() {
        try { btn.scrollIntoView({ behavior: 'smooth', block: 'nearest' }); } catch (e) {}
      }, 80);
    }

    // v3: hide the "Passer cette question" button once user has selected something —
    // committing to a choice and skipping are mutually exclusive actions.
    var skipBtn = document.getElementById('quizSkipLink');
    if (skipBtn) skipBtn.style.display = quizState.selectedAnswers.length > 0 ? 'none' : '';

    // Persist in-progress selections so a refresh mid-multi-select doesn't wipe them
    saveState();
  }

  function handleMultipleChoiceContinue(questionId) {
    if (quizState.selectedAnswers.length === 0) return; // gated by button disabled state, no popup
    if (quizState.isTransitioning) return;
    quizState.isTransitioning = true;

    recordResponse(questionId, quizState.selectedAnswers);

    setTimeout(function() {
      advanceQuiz();
      quizState.isTransitioning = false;
    }, 300);
  }

  // v3: skip a question without recording an answer (used by optional Q2 + Q11 shortcut).
  // Clears any in-progress selections and any prior committed responses for this question
  // so funnel events are clean (no stale answer leaks into BigQuery / Klaviyo).
  function handleQuestionSkip(questionId) {
    if (quizState.isTransitioning) return;
    quizState.isTransitioning = true;

    quizState.selectedAnswers = [];
    quizState.responses = quizState.responses.filter(function(r) { return r.questionId !== questionId; });
    saveState();

    setTimeout(function() {
      advanceQuiz();
      quizState.isTransitioning = false;
    }, 200);
  }

  function recordResponse(questionId, answers) {
    quizState.responses = quizState.responses.filter(function(r) { return r.questionId !== questionId; });

    var answersArray = Array.isArray(answers) ? answers : [answers];

    answersArray.forEach(function(answer) {
      // v2: an answer may carry a `gender` marker (Q2 "Pour personnaliser, vous êtes…")
      if (answer.gender && (answer.gender === 'male' || answer.gender === 'female' || answer.gender === 'unspecified')) {
        quizState.gender = answer.gender === 'unspecified' ? null : answer.gender;
      }
      quizState.responses.push({
        questionId: questionId,
        answerId: answer.id,
        text: answer.text,
        scores: answer.scores || {}
      });
    });

    saveState();
  }

  /**
   * v6 personalisation helpers — used by T1 commitment, T3 loading reveal + redirect,
   * and T4 mid-funnel reveal. All operate on the partial result computable from
   * whatever questions the user has answered so far.
   */
  function getRunningResult() {
    if (typeof window.calculateDetoxCure !== 'function') return null;
    try {
      return window.calculateDetoxCure(quizState.responses || []);
    } catch (e) {
      return null;
    }
  }

  function axisLabel(axis, form) {
    // v6.4 — phase metadata aligned with the canonical Intégrale Détox 3-phase
    // positioning (see sections/adv-reveal-page.liquid + sections/adv-compare-page.liquid
    // for the reference advertorials that drive traffic to this quiz). Users arrive
    // already familiar with "Phase 1 / 2 / 3" — the quiz now mirrors that vocabulary
    // throughout the diagnostic + result.
    var map = {
      foie:     {
        full: 'hépatique',  short: 'foie',      noun: 'foie',       condition: 'foie surchargé',
        phaseNum: 'Phase 1', phaseTitle: 'Purification hépatique',     phaseShort: 'Foie'
      },
      intestin: {
        full: 'digestive',  short: 'intestin',  noun: 'intestin',   condition: 'ventre encombré',
        phaseNum: 'Phase 2', phaseTitle: 'Drainage des émonctoires',   phaseShort: 'Drainage'
      },
      minceur:  {
        full: 'métabolique', short: 'minceur',  noun: 'métabolisme', condition: 'métabolisme ralenti',
        phaseNum: 'Phase 3', phaseTitle: 'Brûle-graisses métabolique', phaseShort: 'Brûle-graisses'
      }
    };
    var entry = map[axis] || map.foie;
    return entry[form || 'full'];
  }

  // v6.4 — How many 30-day phase cycles per cure?
  // C1 Express 30j = 1 cycle | C2 Active 60j = 2 cycles | C3 Transformation 120j = 4 cycles
  function cyclesForCure(cure) {
    var map = { c1: 1, c2: 2, c3: 4 };
    return map[cure] || 1;
  }

  // v6.6 — Bulk-pack savings per cure (vs buying single boxes separately)
  function savingsForCure(cure) {
    var map = (config.cureSavings) || { c1: 0, c2: 30, c3: 100 };
    return map[cure] || 0;
  }

  // v6.6 — Show a small top-right toast announcing an unlocked milestone.
  // Auto-dismisses after the configured duration; fires a BQ event for funnel attribution.
  var _milestoneToastTimer = null;
  function showMilestoneToast(milestone) {
    if (!elements.milestoneToast || !milestone) return;
    // v6.6.1 — never toggle aria-hidden on this aria-live region (would suppress
    // screen-reader announcements). Visual show/hide is via CSS only. Setting the
    // textContent on the labelled-by spans triggers the polite live announcement.
    if (elements.milestoneToastIcon)  elements.milestoneToastIcon.textContent  = milestone.icon || '';
    if (elements.milestoneToastLabel) elements.milestoneToastLabel.textContent = milestone.label || '';
    elements.milestoneToast.classList.add('quiz-milestone-toast--visible');

    if (_milestoneToastTimer) clearTimeout(_milestoneToastTimer);
    _milestoneToastTimer = setTimeout(function() {
      if (!elements.milestoneToast) return;
      elements.milestoneToast.classList.remove('quiz-milestone-toast--visible');
    }, milestone.duration || 3500);

    trackEvent('quiz_milestone_shown', {
      quiz_id: config.quizId,
      milestone_label: milestone.label,
      after_question: milestone.afterQuestion
    });
    // v6.6.1 — also persist to BQ so the funnel dashboard can correlate
    // milestone reveals with completion + conversion (matches the pattern
    // used by commitment_shown / results_summary_shown elsewhere in the file).
    sendToBigQuery('milestone_shown', milestone.afterQuestion);
  }

  // v6.6 — Hook called from advanceQuiz after a question is recorded but before
  // navigating to the next screen. Fires the matching toast at most once per
  // session (guard via quizState.milestonesShown).
  function checkInlineMilestone(currentQ) {
    var inline = config.inlineMilestones || [];
    quizState.milestonesShown = quizState.milestonesShown || {};
    for (var i = 0; i < inline.length; i++) {
      var m = inline[i];
      if (m.afterQuestion === currentQ && !quizState.milestonesShown[currentQ]) {
        quizState.milestonesShown[currentQ] = true;
        saveState();
        showMilestoneToast(m);
        break;
      }
    }
  }

  // v6.3 — Persona key from gender + age. Matches the buckets defined in
  // config.personas (under_45 / 45_54 / 55_64 / 65_plus × female / male).
  function getPersonaKey() {
    var gender = quizState.gender;
    if (!gender || gender === 'unspecified') return 'default';
    var ageText = getAgeRange();
    if (!ageText) return 'default';

    // Map age text to bucket. Tolerant of label variations across versions.
    var bucket;
    if (/(34|24)\s*ans?\s*et?\s*moins/i.test(ageText) || /18-24/i.test(ageText) || /25-34/i.test(ageText)) {
      bucket = 'under_45';
    } else if (/35\s*-\s*44/i.test(ageText)) {
      bucket = 'under_45';
    } else if (/45\s*-\s*54/i.test(ageText)) {
      bucket = '45_54';
    } else if (/55\s*-\s*64/i.test(ageText)) {
      bucket = '55_64';
    } else if (/65\s*ans?\s*et?\s*plus/i.test(ageText)) {
      bucket = '65_plus';
    } else {
      return 'default';
    }
    return gender + '_' + bucket;
  }

  function getPersonaTestimonial(slot) {
    var personas = config.personas || {};
    var key = getPersonaKey();
    var p = personas[key] || personas['default'] || {};
    return p[slot] || null;
  }

  // Does the user have a medication-dependent profile?
  // True if they picked the "traitements médicamenteux" option at the heavy-metals
  // question (Q9 in v6.2, Q7 in v5/v6). Used to surface a compatibility note on T7.
  function hasMedsDependency() {
    var responses = quizState.responses || [];
    for (var i = 0; i < responses.length; i++) {
      var r = responses[i];
      if (r && r.text && /traitements?\s+m[ée]dicament/i.test(r.text)) return true;
    }
    return false;
  }

  function axisStrengthLabel(axisValue) {
    if (axisValue >= 7) return 'marquée';
    if (axisValue >= 4) return 'modérée';
    return 'légère';
  }

  function getGenderLabel(gender) {
    var g = gender || quizState.gender;
    if (g === 'female') return 'femme';
    if (g === 'male')   return 'homme';
    return '';
  }

  function getGenderLabelCap(gender) {
    var l = getGenderLabel(gender);
    return l ? l.charAt(0).toUpperCase() + l.slice(1) : '';
  }

  function getAgeRange() {
    // The age question moved between versions (Q10 in v5/v6/v6.1, Q2 in v6.2).
    // Detect by matching the age-bucket pattern in the answer text instead of relying
    // on a hardcoded question ID. Picks the first response whose text contains an
    // explicit age range like "35-44 ans", "65 ans et plus", "34 ans et moins".
    var responses = quizState.responses || [];
    var agePattern = /\d+\s*(?:-\s*\d+)?\s*ans?(?:\s+(?:et|ou)\s+\w+)?/i;
    for (var i = 0; i < responses.length; i++) {
      var r = responses[i];
      if (r && r.text && agePattern.test(r.text)) {
        return r.text;
      }
    }
    return '';
  }

  function commitmentDaysFor(cure) {
    var d = (config.cureDurations && config.cureDurations[cure]) || '';
    return d || '30 jours';
  }

  function personalisationVars(partial) {
    partial = partial || getRunningResult() || { dominantAxis: 'foie', scores: { foie: 0, intestin: 0, minceur: 0 }, cure: 'c2' };
    var dominantAxis = partial.dominantAxis || 'foie';
    // Normalize axis values to /10 for strength label (matches loading-screen display)
    var displayScores = computeAxisScoresForDisplay();
    var axisVal = displayScores[dominantAxis] || 0;
    var cure = partial.cure || 'c2';
    var cureName = (config.cureNames && config.cureNames[cure]) || cure;
    var cureDuration = (config.cureDurations && config.cureDurations[cure]) || '';
    var cycles = cyclesForCure(cure);
    return {
      dominantAxis: axisLabel(dominantAxis, 'full'),
      dominantAxisFull: axisLabel(dominantAxis, 'full'),
      dominantAxisShort: axisLabel(dominantAxis, 'short'),
      dominantAxisNoun: axisLabel(dominantAxis, 'noun'),
      // v6.3 — concrete body-language framing
      axisCondition: axisLabel(dominantAxis, 'condition'),
      // v6.4 — 3-phase positioning (matches the advertorial vocabulary)
      dominantPhaseNum:   axisLabel(dominantAxis, 'phaseNum'),    // "Phase 1" / "Phase 2" / "Phase 3"
      dominantPhaseTitle: axisLabel(dominantAxis, 'phaseTitle'),  // "Purification hépatique" / …
      dominantPhaseShort: axisLabel(dominantAxis, 'phaseShort'),  // "Foie" / "Drainage" / "Brûle-graisses"
      dominantAxisStrength: axisStrengthLabel(axisVal),
      genderLabel: getGenderLabel(),
      genderLabelCap: getGenderLabelCap(),
      ageRange: getAgeRange(),
      commitmentDays: commitmentDaysFor(cure),
      cureName: cureName,
      cureDuration: cureDuration,
      cycles: cycles,
      cyclesLabel: cycles === 1 ? '1 cycle complet' : cycles + ' cycles complets'
    };
  }

  function interpolate(text, vars) {
    if (!text) return '';
    return String(text).replace(/\{(\w+)\}/g, function(match, key) {
      return (vars && vars[key] != null) ? vars[key] : '';
    });
  }

  /**
   * v6 T1 — Accountability commit screen, shown after Q10 before processResults().
   * Both "yes" and "no" paths advance to processResults; the answer is logged for
   * downstream analytics so we can A/B split conversion rate by commitment.
   */
  function showCommitmentScreen() {
    var cs = (config.commitmentStep) || {};
    var partial = getRunningResult();
    var vars = personalisationVars(partial);
    // v6.6.2 — capture the prior-shown flag BEFORE we set it, so a resume render
    // (loadState path) doesn't double-fire commitment_shown to BQ/GA and tank the
    // yes-rate denominator.
    var alreadyShown = !!quizState.commitmentShown;

    if (elements.commitmentTitle) elements.commitmentTitle.innerHTML = interpolate(cs.title || 'Êtes-vous prêt(e) à passer à l’action ?', vars);
    if (elements.commitmentBody)  elements.commitmentBody.innerHTML  = interpolate(cs.body  || '', vars);
    if (elements.commitmentYesBtn) {
      elements.commitmentYesBtn.textContent = cs.yesText || 'Oui, je m’engage';
      elements.commitmentYesBtn.onclick = function() { onCommitmentAnswer(true); };
    }
    if (elements.commitmentNoBtn) {
      elements.commitmentNoBtn.textContent = cs.noText || 'Plus tard, peut-être';
      elements.commitmentNoBtn.onclick = function() { onCommitmentAnswer(false); };
    }

    quizState.commitmentShown = true;
    saveState();
    showScreen('commitment');

    if (!alreadyShown) {
      trackEvent('quiz_commitment_shown', {
        quiz_id: config.quizId,
        cure: partial && partial.cure,
        dominant_axis: partial && partial.dominantAxis
      });
      sendToBigQuery('commitment_shown', 10);
    }

    // v6.6.3 — only push the commitment marker once. On resume the URL already
    // has ?commitment=1 (since we set it on the first render), so re-pushing
    // creates a duplicate history entry that requires an extra Back click.
    if (!alreadyShown && window.location.search.indexOf('commitment=1') === -1) {
      try {
        safePushState({ screen: 'commitment' }, '', window.location.pathname + '?commitment=1');
      } catch (e) {}
    }
  }

  function onCommitmentAnswer(committed) {
    quizState.commitmentAnswer = committed ? 'yes' : 'no';
    saveState();
    trackEvent('quiz_commitment_answered', {
      quiz_id: config.quizId,
      committed: committed,
      commitment_value: committed ? 'yes' : 'no'
    });
    sendToBigQuery(committed ? 'commitment_yes' : 'commitment_no', 10);
    processResults();
  }

  /**
   * Advance quiz: check for interstitial, next question, or completion
   */
  function advanceQuiz() {
    var currentQ = quizState.currentQuestion;

    // GA: track question completed
    trackEvent('quiz_question', { quiz_id: config.quizId, question: currentQ });

    // v5: 'started' (GA + BQ) fires the first time the user advances past Q1 (they
    // answered and committed). One-shot guarded by quizState.startedSent — without
    // this flag, back-nav → Q1 → forward would re-fire 'started' on every cycle.
    // v<5 fires 'started' in startQuiz() from the welcome CTA click instead.
    if ((config.version || 1) >= 5 && currentQ === 1 && !quizState.startedSent) {
      quizState.startedSent = true;
      saveState();
      trackEvent('quiz_started', { quiz_id: config.quizId, question: 1, gender: quizState.gender });
      sendToBigQuery('started', 1);
    }

    // Send progress to BigQuery (non-blocking, for funnel accuracy)
    sendToBigQuery('progress', currentQ);

    // v6.6 — Check for an inline milestone toast to fire after this question.
    // Fires AFTER the BQ progress event so the milestone view is attributable to
    // the question it followed. Runs in parallel with the screen transition.
    checkInlineMilestone(currentQ);

    // Check if interstitial should be shown after this question
    quizState.interstitialsShown = quizState.interstitialsShown || {};
    var interstitials = config.interstitials || {};
    if (interstitials[currentQ] && !quizState.interstitialsShown[currentQ]) {
      showInterstitial(currentQ);
      return;
    }

    if (currentQ < quizState.totalQuestions) {
      goToNextQuestion();
    } else {
      // v6 T1 — accountability commit interstitial before processResults().
      var cs = config.commitmentStep;
      var v6plus = (config.version || 1) >= 6;
      if (v6plus && cs && cs.enabled && !quizState.commitmentShown) {
        showCommitmentScreen();
        return;
      }
      processResults();
    }
  }

  function showInterstitial(afterQuestion) {
    quizState.interstitialsShown = quizState.interstitialsShown || {};
    if (afterQuestion) quizState.interstitialsShown[afterQuestion] = true;
    // Legacy flag
    quizState.interstitialShown = true;
    saveState();

    // Render interstitial content dynamically
    var interstitials = config.interstitials || {};
    var content = interstitials[afterQuestion];
    if (content && elements.interstitialScreen) {
      // v6 T4 — when revealAxis is set, interpolate {dominantAxisFull} etc. from running scores
      var vars = content.revealAxis ? personalisationVars() : {};
      var fmt = function(s) { return content.revealAxis ? interpolate(s, vars) : s; };
      var ctaText = (content.cta || 'Continuer');

      var html = '';
      if (content.title) html += '<h2>' + fmt(content.title) + '</h2>';
      if (content.paragraphs) {
        content.paragraphs.forEach(function(p) {
          html += '<p>' + fmt(p) + '</p>';
        });
      }
      if (content.highlight) {
        html += '<div class="quiz-interstitial__highlight">' + fmt(content.highlight) + '</div>';
      }
      // v6.2 — testimonial block (breather slide with social proof)
      // v6.3 — prefer persona-matched testimonial when available; fallback to static
      if (content.testimonial) {
        var personaT = getPersonaTestimonial('breather');
        var t = personaT || content.testimonial;
        var starCount = Math.max(0, Math.min(5, t.stars || 5));
        var starHtml = '';
        for (var i = 0; i < starCount; i++) starHtml += '★';
        var verifiedHtml = t.verified
          ? '<span class="quiz-interstitial__verified"><svg width="11" height="11" viewBox="0 0 24 24" fill="none" aria-hidden="true"><circle cx="12" cy="12" r="11" fill="#00b67a"/><path d="M7 12l3.5 3.5L17 9" stroke="#fff" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg>Avis vérifié</span>'
          : '';
        html += '<div class="quiz-interstitial__testimonial">' +
          '<div class="quiz-interstitial__stars">' + starHtml + '</div>' +
          '<p class="quiz-interstitial__quote">« ' + fmt(t.quote || '') + ' »</p>' +
          '<p class="quiz-interstitial__author"><strong>' + fmt(t.author || '') + '</strong>' + verifiedHtml + '</p>' +
        '</div>';
      }
      // v6.2 — urgency block (after-Q8 slide)
      if (content.urgencyBlock) {
        var ub = content.urgencyBlock;
        html += '<div class="quiz-interstitial__urgency">' +
          '<span class="quiz-interstitial__urgency-icon">' + (ub.icon || '⏰') + '</span>' +
          fmt(ub.body || '') +
        '</div>';
      }
      html += '<button class="quiz-btn quiz-btn-primary" id="interstitialContinueBtn">' + ctaText + '</button>';
      elements.interstitialScreen.innerHTML = html;
      // Re-bind continue button
      var btn = document.getElementById('interstitialContinueBtn');
      if (btn) btn.addEventListener('click', continueAfterInterstitial);
      elements.interstitialBtn = btn;
    }

    showScreen('interstitial');

    safePushState({ screen: 'interstitial', after: afterQuestion }, '', window.location.pathname + '?interstitial=' + afterQuestion);
  }

  function continueAfterInterstitial() {
    showScreen('question');
    goToNextQuestion();
  }

  function goToNextQuestion() {
    var nextQuestion = quizState.currentQuestion + 1;

    elements.questionScreen.classList.add('fade-out');

    setTimeout(function() {
      renderQuestion(nextQuestion);
      updateProgress();
      elements.questionScreen.classList.remove('fade-out');

      safePushState({ screen: 'question', question: nextQuestion }, '', window.location.pathname + '?q=' + nextQuestion);
    }, 200);
  }

  function goBack() {
    if (quizState.currentQuestion <= 1) {
      goToWelcome();
      return;
    }

    var prevQuestion = quizState.currentQuestion - 1;

    elements.questionScreen.classList.add('fade-out');

    setTimeout(function() {
      renderQuestion(prevQuestion);
      updateProgress();
      elements.questionScreen.classList.remove('fade-out');

      safePushState({ screen: 'question', question: prevQuestion }, '', window.location.pathname + '?q=' + prevQuestion);
    }, 200);
  }

  function goToWelcome() {
    elements.questionScreen.classList.add('fade-out');

    setTimeout(function() {
      quizState.currentQuestion = 0;
      quizState.responses = [];
      quizState.selectedAnswers = [];
      quizState.interstitialShown = false;
      saveState();

      showScreen('welcome');
      showPreviousResultCta();

      safePushState({ screen: 'welcome' }, '', window.location.pathname);
    }, 200);
  }

  /**
   * Safe pushState wrapper — third-party scripts monkey-patch history.pushState
   * and can throw SecurityError if the URL isn't fully qualified
   */
  function safePushState(state, title, path) {
    try {
      var url = window.location.origin + path;
      history.pushState(state, title, url);
    } catch (e) {
      // Silently fail — pushState is non-critical for quiz functionality
    }
  }

  function handlePopState(event) {
    if (event.state) {
      if (event.state.screen === 'question' && event.state.question) {
        renderQuestion(event.state.question);
        updateProgress();
        showScreen('question');
      } else if (event.state.screen === 'welcome') {
        showScreen('welcome');
        showPreviousResultCta();
      } else if (event.state.screen === 'interstitial') {
        showScreen('interstitial');
      }
    }
  }

  function updateProgress() {
    var progress = (quizState.currentQuestion / quizState.totalQuestions) * 100;
    elements.progressFill.style.width = progress + '%';
  }

  /**
   * Process results: calculate cure, animate loading, redirect
   */
  // v6.3 — Populate the persistent loading-screen testimonial with the persona-matched
  // quote before showing the loading screen.
  function populateLoadingTestimonial() {
    var t = getPersonaTestimonial('loading');
    if (!t) return;
    if (elements.loadingTestimonialQuote && t.quote) {
      elements.loadingTestimonialQuote.textContent = '« ' + t.quote + ' »';
    }
    if (elements.loadingTestimonialAuthor && t.author) {
      elements.loadingTestimonialAuthor.textContent = t.author;
    }
  }

  async function processResults() {
    if (quizState.completed) return;
    populateLoadingTestimonial();
    showScreen('loading');

    var result = window.calculateDetoxCure
      ? window.calculateDetoxCure(quizState.responses)
      : { cure: 'c2', scores: { c1: 0, c2: 0, c3: 0 }, redirectUrl: '/products/integrale-detox' };
    var sessionId = getOrCreateSessionId();

    // v3: pass result so animateLoadingScreen can interpolate {dominantAxis} into step labels
    var totalDuration = animateLoadingScreen(result);

    // Mark complete
    quizState.completed = true;
    saveState();

    // GA: quiz completed
    trackEvent('quiz_completed', { quiz_id: config.quizId, cure: result.cure, cure_name: result.cureName });

    // Google Ads conversion (non-blocking)
    try {
      if (typeof gtag === 'function') {
        gtag('event', 'conversion', { send_to: 'AW-435904212/AS3KCLfrja0cENS97c8B' });
      }
    } catch (e) {
      console.warn('Google Ads conversion event failed:', e);
    }

    // Fire Klaviyo event (non-blocking)
    try {
      if (window.klaviyo && window.klaviyo.push) {
        window.klaviyo.push(['track', 'Quiz Completed', Object.assign({
          quiz_id: config.quizId,
          quiz_version: config.version || 1,
          cure: result.cure,
          cure_name: result.cureName
        }, buildAnalyticsScorePayload(result))]);
        console.log('Klaviyo event fired: Quiz Completed');
      }
    } catch (e) {
      console.warn('Klaviyo event failed:', e);
    }

    // Store all responses in BigQuery (non-blocking)
    try {
      var cloudFunctionUrl = config.cloudFunctionUrl;
      if (cloudFunctionUrl) {
        var ctx = getTrackingContext();

        // Build answers object keyed by question ID
        var answersObj = {};
        quizState.responses.forEach(function(r) {
          var key = 'q' + r.questionId;
          if (answersObj[key]) {
            answersObj[key] = answersObj[key] + ', ' + r.text;
          } else {
            answersObj[key] = r.text;
          }
        });
        // Add metadata
        answersObj._gender = quizState.gender || null;
        answersObj._duration_seconds = quizState.startTime ? Math.round((Date.now() - quizState.startTime) / 1000) : null;
        answersObj._total_questions = quizState.totalQuestions;
        answersObj._cure_name = result.cureName;

        // Store question texts so records are self-contained
        var questionsMap = {};
        config.questions.forEach(function(q) {
          questionsMap['q' + q.id] = q.text;
        });

        // Enrich scores with v2 metadata (total + dominantAxis) so downstream SQL
        // can slice on these without reverse-engineering the cure code.
        var enrichedScores = Object.assign({}, result.scores || {});
        if (result.total != null) enrichedScores._total = result.total;
        if (result.dominantAxis) enrichedScores._dominant_axis = result.dominantAxis;

        fetch(cloudFunctionUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            quiz_id: config.quizId,
            version: config.version || 1,
            session_id: sessionId,
            shopify_customer_id: config.shopifyCustomerId || null,
            answers: answersObj,
            questions: questionsMap,
            scores: enrichedScores,
            result: result.cure,
            identifiers: ctx.identifiers,
            url_params: ctx.url_params,
            context: ctx.context,
            user_agent: navigator.userAgent,
            utm_source: ctx.utm_source,
            utm_medium: ctx.utm_medium,
            utm_campaign: ctx.utm_campaign
          })
        }).catch(function(err) {
          console.warn('BigQuery storage failed:', err);
        });
      }
    } catch (e) {
      console.warn('BigQuery storage error:', e);
    }

    // Store result for cart attributes
    // v6.6.2 — persist the full personalisation snapshot (dominantAxis, gender, age,
    // commitmentAnswer) alongside cure. Previously only cure/cureName/scores were saved,
    // so on reload the "previous result" CTA and writeQuizToCart() fell back to a
    // re-scored empty state (dominantAxis defaulted to 'intestin', gender/age/commit
    // were blank) — which broke axis-based segmentation in cart attributes and the
    // result page URL.
    var quizResultForCart = {
      cure: result.cure,
      cureName: result.cureName,
      scores: result.scores,
      dominantAxis: result.dominantAxis,
      gender: quizState.gender || null,
      ageRange: getAgeRange(),
      commitmentAnswer: quizState.commitmentAnswer || null,
      timestamp: Date.now()
    };
    try {
      sessionStorage.setItem('quizDetoxResult', JSON.stringify(quizResultForCart));
    } catch (e) {
      console.warn('Could not save result to sessionStorage:', e);
    }

    // Kick off the cart attribute write before redirect while the loading
    // screen is still visible. The load-time fallback remains below.
    writeQuizToCart(quizResultForCart, sessionId);

    // Wait for analysis animation
    await new Promise(function(resolve) { setTimeout(resolve, totalDuration); });

    // v6.1 T7 — Results Summary screen between loading and redirect, when enabled.
    // The redirect URL is wired to the screen's CTA button instead of firing immediately.
    if ((config.version || 1) >= 6 && config.resultsSummary && config.resultsSummary.enabled) {
      var redirectUrl = buildRedirectUrl(result.redirectUrl, result.cure);
      showResultsSummary(result, redirectUrl);
      return;
    }

    // Redirect to product page, forwarding all URL params (v<6 path)
    window.location.href = buildRedirectUrl(result.redirectUrl, result.cure);
  }

  /**
   * v6.1 T7 — Results Summary screen renderer. Builds the visual profile card
   * (3 axis bars), solution-reveal narrative, and earned-discount offer copy
   * with full personalisation interpolation. Final CTA fires the redirect.
   */
  function showResultsSummary(result, redirectUrl) {
    var rs = config.resultsSummary || {};
    var displayScores = computeAxisScoresForDisplay();
    var vars = personalisationVars(result);
    // Override cureName/cureDuration with the actual result rather than the running partial
    vars.cureName = result.cureName || vars.cureName;
    vars.cureDuration = result.cureDuration || vars.cureDuration;

    if (elements.resultsIntro) {
      elements.resultsIntro.innerHTML = interpolate(rs.intro || '', vars);
    }

    // v6.4 — 3-phase card. Each axis becomes a Phase row showing:
    //   • Top line: "Phase X — Title"  + ⭐ marker if this is the dominant priority
    //   • Bottom: progress bar (need level out of 10) + hint ("10 jours · cible foie")
    // The dominant axis row gets the orange "high" tint to signal priority regardless
    // of absolute score (a 4/10 dominant phase still matters, it's the user's bottleneck).
    if (elements.resultsAxesContainer) {
      var rawLabels = rs.axisLabels || {};
      // Backward-compat: if config still uses the v6.3 short-string schema
      // ({ foie: 'Foie', … }) wrap each into the v6.4 object schema with axisLabel() fallbacks.
      function labelFor(axis) {
        var L = rawLabels[axis];
        if (L && typeof L === 'object') return L;
        return {
          phaseNum:   axisLabel(axis, 'phaseNum'),
          phaseTitle: typeof L === 'string' ? L : axisLabel(axis, 'phaseTitle'),
          hint: ''
        };
      }
      var dominantAxisKey = (result && result.dominantAxis) || 'foie';
      var axes = ['foie', 'intestin', 'minceur'];
      elements.resultsAxesContainer.innerHTML = '';
      axes.forEach(function(axis) {
        var val = displayScores[axis] || 0; // 0-10
        var pct = Math.max(8, val * 10); // min 8% for visibility even at 0
        var isDominant = axis === dominantAxisKey;
        // Dominant phase always gets the orange "priority" tint; others follow score
        var mod = isDominant ? 'high' : (val >= 7 ? 'high' : val >= 4 ? 'mid' : 'low');
        var info = labelFor(axis);

        var row = document.createElement('div');
        row.className = 'quiz-results__phase-row' + (isDominant ? ' quiz-results__phase-row--priority' : '');

        var head = document.createElement('div');
        head.className = 'quiz-results__phase-head';
        var num = document.createElement('span');
        num.className = 'quiz-results__phase-num';
        num.textContent = info.phaseNum || '';
        var title = document.createElement('span');
        title.className = 'quiz-results__phase-title';
        title.textContent = info.phaseTitle || '';
        head.appendChild(num);
        head.appendChild(title);
        if (isDominant) {
          // v6.4.1 — Diagnostic indicator only. The protocol always runs Phase 1 → 2 → 3
          // in order; the "strong signal" axis informs the CYCLE COUNT (number of boxes),
          // not which phase to attack first. Badge wording reflects that.
          var star = document.createElement('span');
          star.className = 'quiz-results__phase-priority';
          star.textContent = '🎯 Signal fort';
          head.appendChild(star);
        }

        var barRow = document.createElement('div');
        barRow.className = 'quiz-results__phase-bar-row';
        var bar = document.createElement('div');
        bar.className = 'quiz-results__axis-bar';
        var fill = document.createElement('div');
        fill.className = 'quiz-results__axis-fill quiz-results__axis-fill--' + mod;
        fill.style.width = '0%';
        bar.appendChild(fill);
        var v = document.createElement('span');
        v.className = 'quiz-results__axis-value';
        v.textContent = val + '/10';
        barRow.appendChild(bar);
        barRow.appendChild(v);

        row.appendChild(head);
        row.appendChild(barRow);
        if (info.hint) {
          var hint = document.createElement('div');
          hint.className = 'quiz-results__phase-hint';
          hint.textContent = info.hint;
          row.appendChild(hint);
        }

        elements.resultsAxesContainer.appendChild(row);

        // Animate fill on next tick
        requestAnimationFrame(function() {
          setTimeout(function() { fill.style.width = pct + '%'; }, 50);
        });
      });
    }

    if (elements.resultsDominantLabel) {
      elements.resultsDominantLabel.textContent = rs.dominantHeading || 'Votre signal le plus marqué :';
    }
    if (elements.resultsDominantValue) {
      // v6.4 — surface the phase ("Phase 3 — Brûle-graisses métabolique") instead of the
      // bare axis adjective. Matches the advertorial vocabulary the user arrived with.
      elements.resultsDominantValue.textContent = (vars.dominantPhaseNum && vars.dominantPhaseTitle)
        ? (vars.dominantPhaseNum + ' — ' + vars.dominantPhaseTitle)
        : (vars.dominantAxisFull || '');
    }
    if (elements.resultsSolutionTitle) {
      elements.resultsSolutionTitle.innerHTML = interpolate(rs.solutionTitle || '', vars);
    }
    if (elements.resultsSolutionBody) {
      // The solution body sits inside a <p class="quiz-results__solution-body"> in the
      // section template, so we can't nest <p> for the meds note (invalid HTML — browsers
      // auto-close the outer <p>, causing layout drift). Use <span class="...note"> with
      // display:block via CSS instead, and append AFTER the solution body element.
      elements.resultsSolutionBody.innerHTML = interpolate(rs.solutionBody || '', vars);
      // v6.3 — appendix line for medication-dependent profiles (16% of 65+ segment)
      // Inject as a sibling element after the body <p> rather than nesting inside it.
      var existingNote = elements.resultsSolutionBody.parentNode && elements.resultsSolutionBody.parentNode.querySelector('.quiz-results__meds-note');
      if (existingNote) existingNote.remove();
      if (hasMedsDependency() && config.medsCompatNote && elements.resultsSolutionBody.parentNode) {
        var note = document.createElement('div');
        note.className = 'quiz-results__meds-note';
        note.innerHTML = interpolate(config.medsCompatNote, vars);
        elements.resultsSolutionBody.parentNode.insertBefore(note, elements.resultsSolutionBody.nextSibling);
      }
    }
    // v6.5 — milestones block (free shipping + lifetime guarantee). Backwards-
    // compatible with v6.4.x configs that still use offerTitle/offerBody — if no
    // milestones array is configured, fall back to rendering offerTitle/offerBody
    // as a single milestone-style entry.
    if (elements.resultsMilestonesTitle && rs.milestonesTitle) {
      elements.resultsMilestonesTitle.innerHTML = interpolate(rs.milestonesTitle, vars);
    }
    if (elements.resultsMilestonesSub && rs.milestonesSub) {
      elements.resultsMilestonesSub.innerHTML = interpolate(rs.milestonesSub, vars);
    }
    if (elements.resultsMilestonesList) {
      elements.resultsMilestonesList.innerHTML = '';
      // Clone to avoid mutating the config array between renders.
      var milestones = Array.isArray(rs.milestones) ? rs.milestones.slice() : [];
      // Legacy fallback for v6.4.x configs with offerTitle/offerBody
      if (milestones.length === 0 && (rs.offerTitle || rs.offerBody)) {
        milestones = [{ icon: '', label: rs.offerTitle || '', sub: rs.offerBody || '' }];
      }
      // v6.6 — Append savings milestone for C2/C3 (bulk-pack discount vs single).
      var savings = savingsForCure(result && result.cure);
      if (savings > 0) {
        var packLabel = result.cure === 'c3'
          ? 'pack 4 boîtes (vs 4 achats séparés)'
          : 'pack 2 boîtes (vs 2 achats séparés)';
        milestones.push({
          icon: '💰',
          label: 'Économie de ' + savings + '€ débloquée',
          sub: 'Tarif ' + packLabel + ' — appliqué automatiquement'
        });
      }
      milestones.forEach(function(m) {
        var li = document.createElement('li');
        li.className = 'quiz-results__milestone';
        var iconHtml = m.icon ? '<span class="quiz-results__milestone-icon" aria-hidden="true">' + m.icon + '</span>' : '';
        li.innerHTML =
          iconHtml +
          '<span class="quiz-results__milestone-text">' +
            '<span class="quiz-results__milestone-label">' + interpolate(m.label || '', vars) + '</span>' +
            (m.sub ? '<span class="quiz-results__milestone-sub">' + interpolate(m.sub, vars) + '</span>' : '') +
          '</span>';
        elements.resultsMilestonesList.appendChild(li);
      });
    }
    if (elements.resultsCtaBtn) {
      elements.resultsCtaBtn.textContent = rs.ctaText || 'Accéder à ma cure →';
      elements.resultsCtaBtn.onclick = function() {
        trackEvent('quiz_results_summary_cta', {
          quiz_id: config.quizId,
          cure: result.cure,
          dominant_axis: result.dominantAxis
        });
        sendToBigQuery('results_summary_cta', 10);
        window.location.href = redirectUrl;
      };
    }

    trackEvent('quiz_results_summary_shown', {
      quiz_id: config.quizId,
      cure: result.cure,
      dominant_axis: result.dominantAxis,
      commitment: quizState.commitmentAnswer || null
    });
    sendToBigQuery('results_summary_shown', 10);

    showScreen('resultsSummary');
    try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch (e) {}
  }

  function animateLoadingScreen(result) {
    var rawMessages = config.loadingMessages || [];
    var defaultDuration = config.loadingStepDuration || 1.0;

    // v2 structured steps (label + result + scoreKey). Fallback to v1 text-only format.
    var steps = rawMessages.map(function(msg) {
      if (typeof msg === 'string') {
        return { label: msg, result: '\u2713', staticResult: true, duration: defaultDuration };
      }
      if (msg.text && !msg.label) {
        // v1 legacy entry — split "Label... OK" into label + result
        var m = msg.text.replace(/\.\.\.\s*OK\s*$/i, '').trim();
        return { label: m, result: '\u2713', staticResult: true, duration: msg.duration || defaultDuration };
      }
      return {
        label: msg.label || '',
        result: msg.result || '\u2713',
        staticResult: msg.staticResult !== false,
        scoreKey: msg.scoreKey || null,
        duration: msg.duration || defaultDuration
      };
    });

    // Compute axis scores from current responses (normalized to /10)
    var axisScores = computeAxisScoresForDisplay();

    // v3: interpolate {dominantAxis} placeholder in step labels (used by step #6 in v3 config).
    // Resolves to "hépatique" / "digestive" / "métabolique" from result.dominantAxis.
    // Falls back to deriving from displayed axis scores when no result is passed (debug paths).
    var dominantAxis = (result && result.dominantAxis) || null;
    if (!dominantAxis) {
      dominantAxis = 'foie';
      if (axisScores.intestin > axisScores[dominantAxis]) dominantAxis = 'intestin';
      if (axisScores.minceur  > axisScores[dominantAxis]) dominantAxis = 'minceur';
    }
    var dominantAxisLabels = { foie: 'hépatique', intestin: 'digestive', minceur: 'métabolique' };
    var dominantAxisLabel = dominantAxisLabels[dominantAxis] || '';
    // v6 T3 — also expose gender + age range for the personalised reveal step
    var loadingVars = {
      dominantAxis: dominantAxisLabel,
      genderLabel: getGenderLabel(),
      ageRange: getAgeRange()
    };
    steps.forEach(function(s) {
      if (s.label && s.label.indexOf('{') !== -1) {
        s.label = interpolate(s.label, loadingVars);
        // Clean up "femme 35-44 ans —" patterns when gender/age are empty
        s.label = s.label.replace(/\s+—\s+surcharge/, ' — surcharge')
                         .replace(/Profil détox\s*:\s*— surcharge/, 'Profil détox : surcharge')
                         .replace(/Profil détox\s*:\s+—/, 'Profil détox :');
      }
    });

    // Compute total duration AFTER any label changes (no impact since durations are fixed,
    // but kept here for clarity that all step prep is done before timing starts).
    var totalDuration = steps.reduce(function(sum, s) { return sum + (s.duration * 1000); }, 0);

    var container = document.getElementById('loadingSteps');
    if (!container) {
      // v1 template fallback — use legacy loadingMessage element
      if (elements.loadingMessage) elements.loadingMessage.textContent = steps[0] ? steps[0].label : '';
      kickProgressFill(totalDuration);
      return totalDuration;
    }

    // Clear any prior render (for ?q=loader re-triggering)
    container.innerHTML = '';

    // Build DOM for each step (all hidden initially)
    var stepEls = steps.map(function(step) {
      var row = document.createElement('div');
      row.className = 'quiz-loading-step';

      var prefix = document.createElement('span');
      prefix.className = 'quiz-loading-step__prefix';
      prefix.textContent = '\u25b8';

      var label = document.createElement('span');
      label.className = 'quiz-loading-step__label';
      label.textContent = step.label;

      var dots = document.createElement('span');
      dots.className = 'quiz-loading-step__dots';
      dots.textContent = '\u2022 \u2022 \u2022';

      var result = document.createElement('span');
      result.className = 'quiz-loading-step__result';

      row.appendChild(prefix);
      row.appendChild(label);
      row.appendChild(dots);
      row.appendChild(result);
      container.appendChild(row);

      return { row: row, result: result, step: step };
    });

    // Reveal each step sequentially, auto-scrolling the container as it fills
    var accumulated = 0;
    stepEls.forEach(function(el, idx) {
      var appearAt = accumulated;
      var completeAt = accumulated + Math.max(200, (el.step.duration * 1000) - 250);
      accumulated += el.step.duration * 1000;

      setTimeout(function() {
        el.row.classList.add('is-visible', 'is-processing');
        // Keep the newest step in view via page-level scroll (older ones stay above)
        try { el.row.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e) {}
      }, appearAt);

      setTimeout(function() {
        el.row.classList.remove('is-processing');
        el.row.classList.add('is-complete');
        applyStepResult(el, axisScores);
      }, completeAt);
    });

    kickProgressFill(totalDuration);

    return totalDuration;
  }

  // Fill the thin progress bar at the bottom over the total duration
  function kickProgressFill(totalDuration) {
    if (!elements.loadingProgressFill) return;
    elements.loadingProgressFill.style.width = '0%';
    var startTime = Date.now();
    var progressInterval = setInterval(function() {
      var elapsed = Date.now() - startTime;
      var progress = Math.min((elapsed / totalDuration) * 100, 100);
      elements.loadingProgressFill.style.width = progress.toFixed(2) + '%';
      if (progress >= 100) clearInterval(progressInterval);
    }, 16);
  }

  // Apply the result for a completed step (✓ / fixed text / animated score X/10)
  function applyStepResult(el, axisScores) {
    var step = el.step;
    var resultEl = el.result;

    if (step.scoreKey && axisScores[step.scoreKey] !== undefined) {
      var target = axisScores[step.scoreKey];
      animateScoreCount(resultEl, 0, target, 700, '/10');
      return;
    }

    if (step.result === '\u2713') {
      resultEl.classList.add('is-check');
      resultEl.textContent = '\u2713';
    } else {
      resultEl.textContent = step.result;
    }
  }

  // Animate a number from -> to over duration ms, appending suffix
  function animateScoreCount(el, from, to, duration, suffix) {
    var start = Date.now();
    var step = function() {
      var elapsed = Date.now() - start;
      var t = Math.min(elapsed / duration, 1);
      var eased = 1 - Math.pow(1 - t, 2); // ease-out quadratic
      var val = Math.round(from + (to - from) * eased);
      el.textContent = val + suffix;
      if (t < 1) requestAnimationFrame(step);
      else el.textContent = to + suffix;
    };
    requestAnimationFrame(step);
  }

  // Build a flat analytics payload of scores for Klaviyo / other integrations.
  // Emits v2 3-axes fields when available, otherwise legacy v1 cure scores.
  function buildAnalyticsScorePayload(result) {
    var s = (result && result.scores) || {};
    var isThreeAxes = ('foie' in s) || ('intestin' in s) || ('minceur' in s);
    if (isThreeAxes) {
      return {
        score_foie:     s.foie     || 0,
        score_intestin: s.intestin || 0,
        score_minceur:  s.minceur  || 0,
        score_total:    (result && result.total != null) ? result.total : (s.foie || 0) + (s.intestin || 0) + (s.minceur || 0),
        dominant_axis:  result && result.dominantAxis
      };
    }
    return {
      score_c1: s.c1 || 0,
      score_c2: s.c2 || 0,
      score_c3: s.c3 || 0
    };
  }

  // Debug helper: seed plausible responses so ?q=loader produces visible axis scores.
  // v2 post-v2 reorder: Q1=symptoms, Q2=projection, Q3=gender, Q4=foie, Q5=matin,
  // Q6=digestion, Q7=métaux, Q8=goal, Q9=tentatives, Q10=age.
  function seedFakeResponsesForLoaderPreview() {
    quizState.responses = [
      { questionId: 1,  answerId: 'A', text: 'Lourdeur apr\u00e8s les repas', scores: { foie: 3, intestin: 1, minceur: 0 } },
      { questionId: 1,  answerId: 'B', text: 'Ballonnements',                scores: { foie: 0, intestin: 4, minceur: 1 } },
      { questionId: 1,  answerId: 'E', text: 'Kilos qui r\u00e9sistent',       scores: { foie: 0, intestin: 1, minceur: 5 } },
      { questionId: 2,  answerId: 'C', text: 'Probablement (projection)',    scores: { foie: 2, intestin: 2, minceur: 2 } },
      { questionId: 4,  answerId: 'C', text: 'Souvent',                      scores: { foie: 3, intestin: 1, minceur: 0 } },
      { questionId: 5,  answerId: 'B', text: 'Un peu p\u00e2teux\u00b7se',     scores: { foie: 2, intestin: 1, minceur: 0 } },
      { questionId: 6,  answerId: 'C', text: 'Lente et lourde',              scores: { foie: 1, intestin: 2, minceur: 0 } },
      { questionId: 7,  answerId: 'A', text: 'Ville',                        scores: { foie: 1, intestin: 1, minceur: 0 } },
      { questionId: 7,  answerId: 'D', text: 'Traitements',                  scores: { foie: 2, intestin: 1, minceur: 0 } },
      { questionId: 8,  answerId: 'C', text: 'Silhouette 5-10kg',            scores: { foie: 0, intestin: 1, minceur: 4 } },
      { questionId: 10, answerId: 'D', text: '45-54 ans',                    scores: { foie: 1, intestin: 1, minceur: 1 } }
    ];
  }

  // Compute per-axis scores from current responses, normalized to /10
  function computeAxisScoresForDisplay() {
    var axes = { foie: 0, intestin: 0, minceur: 0 };
    (quizState.responses || []).forEach(function(r) {
      if (r.scores) {
        axes.foie     += r.scores.foie     || 0;
        axes.intestin += r.scores.intestin || 0;
        axes.minceur  += r.scores.minceur  || 0;
      }
    });
    var maxes = (config.loadingScoreMaxes) || { foie: 34, intestin: 29, minceur: 23 };
    // No Math.max(1, …) floor — a true 0 on an axis is honest signal and reinforces
    // the "this diagnostic is calibrated for YOU" framing.
    return {
      foie:     Math.min(10, Math.max(0, Math.round((axes.foie     / maxes.foie)     * 10))),
      intestin: Math.min(10, Math.max(0, Math.round((axes.intestin / maxes.intestin) * 10))),
      minceur:  Math.min(10, Math.max(0, Math.round((axes.minceur  / maxes.minceur)  * 10)))
    };
  }

  /**
   * Track event via dataLayer (GTM/GA4)
   */
  function trackEvent(eventName, params) {
    try {
      // Use gtag() if available (native GA4), fallback to dataLayer
      if (typeof gtag === 'function') {
        gtag('event', eventName, params || {});
      } else {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(Object.assign({ event: eventName }, params));
      }
    } catch (e) {}
  }

  /**
   * Log welcome_viewed once per browser tab session.
   * Dedup via sessionStorage flag — survives reload + popstate, resets on tab close.
   * Skips entirely if sessionStorage is unavailable (private browsing / quota) to
   * avoid spamming BQ on every render. Denominator for welcome→start funnel rate.
   *
   * Calls getOrCreateSessionId() first because welcome_viewed fires before
   * startQuiz() (which is what creates the session ID otherwise). Without
   * this, every welcome_viewed lands in BQ with session_id = NULL, breaking
   * the welcome→start join.
   */
  function logWelcomeViewed() {
    try {
      if (sessionStorage.getItem('quizDetoxWelcomeLogged')) return;
      sessionStorage.setItem('quizDetoxWelcomeLogged', '1');
    } catch (e) {
      return;
    }
    getOrCreateSessionId();
    trackEvent('quiz_welcome_viewed', { quiz_id: config.quizId });
    sendToBigQuery('welcome_viewed', 0);
  }

  /**
   * Build tracking context (UTMs, URL params, cookie identifiers) for BQ
   * payloads. Must be included on every event — not just completion — so that
   * source can be attributed to welcome_viewed / started / progress / abandoned
   * rows. Reads originalParams (captured at module init) merged with the
   * pv_touch_journal_v2 touch journal (set by snippets/utm-capture.liquid).
   */
  function getTrackingContext() {
    var allUrlParams = {};
    originalParams.forEach(function(value, key) {
      if (key !== 'q' && key !== 'interstitial' && key !== 'force') {
        allUrlParams[key] = value;
      }
    });
    try {
      // Cross-page UTM fallback: last param-bearing touch of the utm-capture v2 journal.
      // Single touch only — blending touches would recreate the gclid+fbclid contamination v2 eliminates.
      var journal = JSON.parse(localStorage.getItem('pv_touch_journal_v2') || 'null');
      var trackingParams = {};
      if (journal && Object.prototype.toString.call(journal.touches) === '[object Array]') {
        for (var ti = journal.touches.length - 1; ti >= 0; ti--) {
          var tpp = journal.touches[ti] && journal.touches[ti].p;
          if (tpp && typeof tpp === 'object' && !Array.isArray(tpp) && Object.keys(tpp).length) { trackingParams = tpp; break; }
        }
      }
      Object.keys(trackingParams).forEach(function(k) {
        if (!allUrlParams[k]) allUrlParams[k] = trackingParams[k];
      });
    } catch (e) {}

    var identifiers = {};
    var trackedCookies = [
      '_shopify_y', '_shopify_s', 'shopify_client_id',
      '_fbp', '_ga', '_gcl_au',
      '_ttp', '_pin_unauth', 'optiMonkClientId', '_dcid'
    ];
    document.cookie.split(';').forEach(function(c) {
      var parts = c.trim().split('=');
      var name = parts[0];
      var value = parts.slice(1).join('=');
      if (trackedCookies.indexOf(name) !== -1 && value) {
        identifiers[name] = value;
      }
    });
    try {
      // kla_id = decoded $exchange_id from __kla_id (not the raw base64 cookie) — equality-joins with orders' kla_id attribute
      var klaMatch = document.cookie.match(/(?:^|;\s*)__kla_id=([^;]+)/);
      if (klaMatch) {
        var klaObj = JSON.parse(atob(decodeURIComponent(klaMatch[1])));
        var klaVal = klaObj && (klaObj.$exchange_id || klaObj.exchange_id);
        if (klaVal) identifiers.kla_id = String(klaVal).substring(0, 200);
      }
    } catch (e) {}

    // Device + page context. Stored as a single JSON blob in BQ for flexibility:
    // referrer = fallback attribution when UTMs are stripped (deep-links, webviews).
    // connection_type = effective network speed for mobile welcome-bounce diagnosis.
    // viewport / screen / language = device + locale classification.
    // page_path is the URL pathname only (no query / fragment). Excluding the
    // query string avoids (a) duplicating url_params, (b) capturing internal
    // quiz state (?q=N, ?interstitial=N) that safePushState pushes during the
    // quiz, and (c) any upstream PII/tokens that might land in the query.
    var context = {
      referrer: document.referrer || null,
      page_path: window.location.pathname,
      language: navigator.language || null,
      viewport_width: window.innerWidth || null,
      viewport_height: window.innerHeight || null,
      screen_width: (typeof screen !== 'undefined' && screen.width) || null,
      screen_height: (typeof screen !== 'undefined' && screen.height) || null,
      connection_type: (navigator.connection && navigator.connection.effectiveType) || null
    };

    return {
      url_params: allUrlParams,
      utm_source: allUrlParams.utm_source || null,
      utm_medium: allUrlParams.utm_medium || null,
      utm_campaign: allUrlParams.utm_campaign || null,
      identifiers: identifiers,
      context: context
    };
  }

  /**
   * Send quiz progress to BigQuery (start/abandon/completed)
   */
  function sendToBigQuery(status, lastQuestion) {
    var cloudFunctionUrl = config.cloudFunctionUrl;
    if (!cloudFunctionUrl) return;

    var sessionId = sessionStorage.getItem('quizSessionId') || '';
    var ctx = getTrackingContext();
    // Progress / abandoned events: scores are empty — they're only meaningful on completion.
    // The CF stores `scores` as a JSON blob, so an empty object is valid for non-completion writes.
    var payload = {
      quiz_id: config.quizId,
      version: config.version || 1,
      session_id: sessionId,
      shopify_customer_id: config.shopifyCustomerId || null,
      answers: {},
      scores: {},
      result: status,
      user_agent: navigator.userAgent,
      utm_source: ctx.utm_source,
      utm_medium: ctx.utm_medium,
      utm_campaign: ctx.utm_campaign,
      url_params: ctx.url_params,
      identifiers: ctx.identifiers,
      context: ctx.context
    };

    // Add partial answers if available
    if (quizState.responses.length > 0) {
      var answersObj = {};
      quizState.responses.forEach(function(r) {
        var key = 'q' + r.questionId;
        answersObj[key] = answersObj[key] ? answersObj[key] + ', ' + r.text : r.text;
      });
      payload.answers = answersObj;
    }

    // Add last_question as metadata
    payload.answers._last_question = lastQuestion;
    payload.answers._status = status;
    payload.answers._gender = quizState.gender || null;

    if (status === 'abandoned') {
      // Use sendBeacon with JSON Blob (plain string sends as text/plain which GCF won't parse)
      navigator.sendBeacon(
        cloudFunctionUrl,
        new Blob([JSON.stringify(payload)], { type: 'application/json' })
      );
    } else {
      // v6.6.2 — keepalive: true survives navigation/unload. Crucial for
      // results_summary_cta (sends event then immediately redirects), and harmless
      // for in-flow events. Spec-capped at 64 KB total in-flight, but our payloads
      // are far under that.
      fetch(cloudFunctionUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true
      }).catch(function() {});
    }
  }

  // Track quiz abandonment on page unload
  // Use multiple events for reliability: beforeunload (desktop), pagehide (mobile Safari),
  // visibilitychange (background tab close). Guard against double-firing.
  var abandonSent = false;
  function sendAbandonBeacon() {
    if (abandonSent || quizState.currentQuestion <= 0 || quizState.completed) return;
    abandonSent = true;
    trackEvent('quiz_abandoned', {
      quiz_id: config.quizId,
      last_question: quizState.currentQuestion
    });
    sendToBigQuery('abandoned', quizState.currentQuestion);
  }
  window.addEventListener('beforeunload', sendAbandonBeacon);
  window.addEventListener('pagehide', sendAbandonBeacon);
  document.addEventListener('visibilitychange', function() {
    if (document.visibilityState === 'hidden') sendAbandonBeacon();
  });

  function showScreen(screenName) {
    var screens = {
      welcome: elements.welcomeScreen,
      question: elements.questionScreen,
      interstitial: elements.interstitialScreen,
      commitment: elements.commitmentScreen,
      loading: elements.loadingScreen,
      resultsSummary: elements.resultsSummaryScreen,
      results: elements.resultsScreen
    };

    Object.values(screens).forEach(function(screen) {
      if (screen) {
        screen.classList.remove('active');
        screen.classList.remove('fade-out');
      }
    });

    if (screens[screenName]) {
      screens[screenName].classList.add('active');
    }

    if (screenName === 'question') {
      elements.progressBar.style.display = 'block';
    } else {
      elements.progressBar.style.display = 'none';
    }
  }

  /**
   * Build redirect URL with forwarded params and quiz_cure.
   * v6 T3 — also forward personalisation params so the result page can interpolate
   * gender / age / dominant axis into its headline.
   */
  function buildRedirectUrl(baseUrl, cure, storedResult) {
    // v6.3 — defensive fallback if scorer returned malformed data (no redirectUrl)
    if (!baseUrl) baseUrl = config.redirectUrl || '/products/integrale-detox';
    // Use original URL params (saved before pushState changes them)
    var currentParams = originalSearch.replace('?', '');
    var forwardParams = currentParams.split('&').filter(function(p) {
      return p && !p.startsWith('q=') && !p.startsWith('interstitial') && !p.startsWith('force')
        && !p.startsWith('commitment=');
    }).join('&');
    // v6.6.3 — when a storedResult is passed at all, treat it as authoritative for the
    // cure being redirected to (Previous-result CTA path). Fall back per-field so a
    // pre-v6.6.2 snapshot (missing dominantAxis/gender/age/commit) doesn't silently
    // pull empty-state defaults from a re-scored blank quizState — previously the
    // axis would default to 'intestin' on those legacy snapshots.
    var hasStored = !!storedResult;
    var live = getRunningResult() || {};
    var partial = (hasStored && storedResult.dominantAxis) ? storedResult : live;
    var gender  = (hasStored && storedResult.gender)            || quizState.gender || null;
    var ageRange = (hasStored && storedResult.ageRange)         || getAgeRange();
    var commit   = (hasStored && storedResult.commitmentAnswer) || quizState.commitmentAnswer || null;
    // Suppress legacy-snapshot fallback to 'intestin' default: if we're on the stored
    // path and the snapshot has no axis, just omit quiz_axis from the URL rather than
    // emit a misleading value.
    var emitAxis = (hasStored && storedResult.dominantAxis) ||
                   (!hasStored && live.dominantAxis &&
                    quizState.responses && quizState.responses.length > 0);

    var pieces = ['quiz_cure=' + encodeURIComponent(cure || '')];
    if (emitAxis && partial.dominantAxis) pieces.push('quiz_axis=' + encodeURIComponent(partial.dominantAxis));
    if (gender)                pieces.push('quiz_gender=' + encodeURIComponent(gender));
    if (ageRange)              pieces.push('quiz_age=' + encodeURIComponent(ageRange));
    if (commit)                pieces.push('quiz_commit=' + encodeURIComponent(commit));
    // v6.2 — forward Shopify discount code so the "earned -10%" framing on the results
    // summary is real (Shopify auto-applies ?discount= at cart/checkout). Admin sets
    // config.discountCode after creating the matching code in Shopify Discounts.
    if (config.discountCode) pieces.push('discount=' + encodeURIComponent(config.discountCode));
    if (config.hidePopupOnRedirect) pieces.push('om_exclude=true');
    var quizParams = pieces.join('&');
    var allParams = [forwardParams, quizParams].filter(Boolean).join('&');
    return baseUrl + (baseUrl.indexOf('?') !== -1 ? '&' : '?') + allParams;
  }

  function showPreviousResultCta() {
    var resultJson = sessionStorage.getItem('quizDetoxResult');
    if (!resultJson) return;

    try {
      var result = JSON.parse(resultJson);
      // v6.6.2 — pass the stored snapshot so the URL carries the cure's original
      // personalisation (axis/gender/age/commit), not a re-scored empty state.
      var redirectUrl = buildRedirectUrl(config.redirectUrl || '/products/integrale-detox', result.cure, result);

      // Only show if not already added
      if (document.getElementById('previousResultCta')) return;

      var ctaDiv = document.createElement('a');
      ctaDiv.id = 'previousResultCta';
      ctaDiv.href = redirectUrl;
      ctaDiv.style.cssText = 'display: flex; align-items: center; justify-content: space-between; margin-top: 20px; padding: 14px 20px; background: linear-gradient(135deg, rgba(16,132,116,0.08), rgba(16,132,116,0.04)); border: 1px solid rgba(16,132,116,0.15); border-radius: 12px; text-decoration: none; font-family: Poppins, sans-serif; transition: all 0.2s;';
      ctaDiv.onmouseover = function() { this.style.background = 'linear-gradient(135deg, rgba(16,132,116,0.12), rgba(16,132,116,0.06))'; this.style.borderColor = 'rgba(16,132,116,0.25)'; };
      ctaDiv.onmouseout = function() { this.style.background = 'linear-gradient(135deg, rgba(16,132,116,0.08), rgba(16,132,116,0.04))'; this.style.borderColor = 'rgba(16,132,116,0.15)'; };
      ctaDiv.innerHTML = '<div style="display:flex;flex-direction:column;gap:2px;"><span style="font-size:0.75em;color:#999;">Vous avez d\u00e9j\u00e0 r\u00e9alis\u00e9 le test\u00a0:</span><span style="font-size:0.88em;font-weight:600;color:#108474;">D\u00e9couvrir ma ' + (result.cureName || 'cure') + '</span></div><span style="font-size:1.2em;color:#108474;">\u2192</span>';

      // Insert after press logos
      var press = document.querySelector('.quiz-press');
      if (press && press.parentNode) {
        press.parentNode.insertBefore(ctaDiv, press.nextSibling);
      }
    } catch (e) {
      // Ignore parse errors
    }
  }

  // Build a compact signature for the active quiz schema so in-progress state
  // is invalidated whenever question order/meaning changes within the same
  // config version (common during v2 iteration).
  function getQuizSchemaSignature() {
    var payload = {
      quizId: (config && config.quizId) || '',
      version: (config && config.version) || 1,
      scoringMode: (config && config.scoringMode) || 'legacy',
      questions: ((config && config.questions) || []).map(function(q) {
        return {
          id: q.id,
          text: q.text || '',
          subtitle: q.subtitle || '',
          format: q.format || '',
          multiple: !!q.multiple,
          maxSelect: q.maxSelect || null,
          answers: (q.answers || []).map(function(a) {
            return {
              id: a.id,
              text: a.text || '',
              exclusive: !!a.exclusive,
              gender: a.gender || null,
              scores: a.scores || {}
            };
          })
        };
      })
    };

    var json = JSON.stringify(payload);
    var hash = 2166136261; // FNV-1a 32-bit
    for (var i = 0; i < json.length; i++) {
      hash ^= json.charCodeAt(i);
      hash += (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24);
    }
    return 'qs_' + (hash >>> 0).toString(16);
  }

  function saveState() {
    try {
      // Never persist transient flags. Stamp with config version so stale v1 state
      // (or older v2 drafts with different Q IDs) can be detected + discarded on load.
      var toSave = Object.assign({}, quizState, {
        isTransitioning: false,
        _quizVersion: (config && config.version) || 1,
        _quizId: (config && config.quizId) || 'detox_integrale',
        _quizSchemaSignature: getQuizSchemaSignature()
      });
      sessionStorage.setItem('quizDetoxState', JSON.stringify(toSave));
    } catch (e) {
      console.error('Failed to save state:', e);
    }
  }

  function loadState() {
    try {
      var saved = sessionStorage.getItem('quizDetoxState');
      if (saved) {
        var parsed = JSON.parse(saved);
        // Invalidate if the saved session has no version stamp (pre-v2 session) or
        // stamps differ from the current config. Prevents stale answer IDs being
        // rehydrated into renumbered v2 questions or across quiz switches.
        var savedVersion = parsed._quizVersion;
        var savedQuizId  = parsed._quizId;
        var savedSchemaSignature = parsed._quizSchemaSignature;
        var currentVersion = (config && config.version) || 1;
        var currentQuizId  = (config && config.quizId) || 'detox_integrale';
        var currentSchemaSignature = getQuizSchemaSignature();
        if (savedVersion !== currentVersion ||
            savedQuizId !== currentQuizId ||
            savedSchemaSignature !== currentSchemaSignature) {
          sessionStorage.removeItem('quizDetoxState');
          return;
        }
        // Strip meta keys before merging into quizState
        delete parsed._quizVersion;
        delete parsed._quizId;
        delete parsed._quizSchemaSignature;
        Object.assign(quizState, parsed);
        quizState.isTransitioning = false; // Always reset on page load

        // Recalculate interstitialsShown based on current question position
        var interstitials = config.interstitials || {};
        quizState.interstitialsShown = {};
        Object.keys(interstitials).forEach(function(qNum) {
          if (quizState.currentQuestion > parseInt(qNum, 10)) {
            quizState.interstitialsShown[qNum] = true;
          }
        });

        if (quizState.completed) {
          quizState.currentQuestion = 0;
          quizState.responses = [];
          quizState.selectedAnswers = [];
          quizState.completed = false;
          quizState.startedSent = false;
          quizState.interstitialShown = false;
          quizState.interstitialsShown = {};
          quizState.commitmentShown = false;
          quizState.commitmentAnswer = null;
          quizState.milestonesShown = {};
          quizState.isTransitioning = false;
          // v6.6.2 — drop session-scoped keys so a retake gets a fresh quizSessionId.
          // Without this, the next startQuiz() reuses the completed attempt's session_id
          // and dashboard session-based filters bleed previous events into the new run.
          sessionStorage.removeItem('quizSessionId');
          sessionStorage.removeItem('quizDetoxCartWritten');
          // v6.6.3 — also reset the welcome-once guard so the retake's new session_id
          // gets paired with its own welcome_viewed row.
          sessionStorage.removeItem('quizDetoxWelcomeLogged');
          saveState();
          showScreen('welcome');
          showPreviousResultCta();
          return;
        }

        // v6.6.2 — resume directly on the commitment screen if it was shown but not
        // answered before the reload. Otherwise we'd skip the commitment entirely on
        // resume (advanceQuiz() short-circuits when commitmentShown is true), losing the
        // commitment_yes/no event after the commitment_shown fired.
        if (quizState.commitmentShown && !quizState.commitmentAnswer) {
          showCommitmentScreen();
          return;
        }

        if (quizState.currentQuestion > 0 && quizState.currentQuestion <= quizState.totalQuestions) {
          showScreen('question');
          renderQuestion(quizState.currentQuestion);
          updateProgress();
        }
      }
    } catch (e) {
      console.error('Failed to load state:', e);
    }
  }

  function setViewportHeight() {
    var vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty('--vh', vh + 'px');
  }

  function getOrCreateSessionId() {
    var sessionId = sessionStorage.getItem('quizSessionId');
    if (!sessionId) {
      sessionId = typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(36) + Math.random().toString(36).substr(2);
      sessionStorage.setItem('quizSessionId', sessionId);
    }
    return sessionId;
  }

  window.resetDetoxQuiz = function() {
    sessionStorage.removeItem('quizDetoxState');
    // v6.6.2 — see restartQuizBtn handler above for the rationale on session_id reset.
    sessionStorage.removeItem('quizSessionId');
    sessionStorage.removeItem('quizDetoxCartWritten');
    // v6.6.3 — see restartQuizBtn handler above for the rationale on welcome flag reset.
    sessionStorage.removeItem('quizDetoxWelcomeLogged');
    window.location.reload();
  };

  // Write quiz result to cart attributes (uses same pattern as theme.liquid UTM tracking)
  // Namespaced by quiz_id so multiple quizzes don't overwrite each other
  // Only writes if quiz was completed in this session (sessionStorage)
  function writeQuizToCart(result, sessionId) {
    var currentResult = result;
    if (!currentResult) {
      var resultJson = sessionStorage.getItem('quizDetoxResult');
      if (!resultJson) return Promise.resolve(false);

      try {
        currentResult = JSON.parse(resultJson);
      } catch (e) {
        return Promise.resolve(false);
      }
    }

    var currentSessionId = sessionId || sessionStorage.getItem('quizSessionId') || '';
    if (!currentSessionId) return Promise.resolve(false);

    // Only write once per quiz completion — check if already written for this session
    if (sessionStorage.getItem('quizDetoxCartWritten') === currentSessionId) {
      return Promise.resolve(true);
    }

    // Namespace attributes by quiz_id so multiple quizzes coexist.
    // v6 T3 — extend with personalisation signals so order-side analytics + Klaviyo flows
    // can segment / interpolate based on the user's profile, not just the recommended cure.
    var prefix = 'quiz_detox_integrale_';
    var quizAttrs = {};
    quizAttrs[prefix + 'session_id'] = currentSessionId;
    quizAttrs[prefix + 'cure_name'] = currentResult.cureName;
    // v6.6.2 — fall back to the stored snapshot when live quizState is empty
    // (the writeQuizToCart() reload path runs at boot before any answer is given).
    var axis    = currentResult.dominantAxis;
    var gender  = quizState.gender           || currentResult.gender;
    var age     = getAgeRange()              || currentResult.ageRange;
    var commit  = quizState.commitmentAnswer || currentResult.commitmentAnswer;
    if (axis)    quizAttrs[prefix + 'axis']    = axis;
    if (gender)  quizAttrs[prefix + 'gender']  = gender;
    if (age)     quizAttrs[prefix + 'age']     = age;
    if (commit)  quizAttrs[prefix + 'commit']  = commit;
    if (config.version) quizAttrs[prefix + 'version'] = String(config.version);

    return fetch('/cart.js', {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    })
    .then(function(r) { return r.json(); })
    .then(function(cart) {
      var merged = Object.assign({}, cart.attributes || {}, quizAttrs);
      return fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ attributes: merged })
      });
    })
    .then(function() {
      sessionStorage.setItem('quizDetoxCartWritten', currentSessionId);
      console.log('Quiz result written to cart attributes');
      return true;
    })
    .catch(function(err) {
      console.warn('Failed to write quiz to cart:', err);
      return false;
    });
  }

  writeQuizToCart();

})();
